import * as React from 'react'
import { getResourceId, Resource, transformToResourceSelectorProps } from '../../../common/resource/model'
import { transformToEnvironmentSelectorProps } from '../../../common/environment/model'
import { ValueProps as ResourceSelectorProps } from '@mulesoft/monitoring-center-components/lib/components/Selector/ResourceSelector/ResourceSelector'
import { ValueProps as VersionSelectorProps } from '@mulesoft/monitoring-center-components/lib/components/Selector/VersionSelector/VersionSelector'
import { transformToVersionSelectorProps } from '../../../common/version/model'
import { EnvironmentSelectorState } from '../../../common/environment/reducer'
import { ResourceSelectorState } from '../../../common/resource/reducer'
import { VersionSelectorState } from '../../../common/version/reducer'
import { default as Button } from '@mulesoft/anypoint-components/lib/Button'
import { API, Environment } from '../../../remote-api/models'
import { ResourceType } from '@mulesoft/monitoring-center-components/lib/models/resource'
import { BuiltinSvg } from '../svgs/BuiltinSvg'
import {
  ValueProps as EnvironmentSelectorProps
} from '@mulesoft/monitoring-center-components/lib/components/Selector/EnvironmentSelector/EnvironmentSelector'
import MonitoringSelector from '@mulesoft/monitoring-center-components/lib/components/Selector/MonitoringSelector/MonitoringSelector'
import './BuiltInDashboardLandingPage.css'
import { SupportedRuntimesTooltipResolver } from '../../../util/SupportedRuntimesHelper'
import CertificatesExpiryBanner from '../../CertificatesExpiryBanner/CertificatesExpiryBanner'
import AnnouncementBanner from '../../../common/components/AnnouncementBanner/AnnouncementBanner'

export type ValueProps = {
  organizationId: string
  environmentSelector: EnvironmentSelectorState
  resourceSelector: ResourceSelectorState
  versionSelector: VersionSelectorState
  isTitanium: boolean
}

export type EventProps = {
  checkBuiltInDashboardsState: () => void
  environmentSelected: (environmentId: string, environments?: Environment[]) => void
  resourceSelected: (resourceId: string, resourceType: ResourceType, resources?: Resource[]) => void
  versionSelected: (versionId: string, versions: API[]) => void
  onView: (orgId: string, envId: string, resourceId: string, versionId?: string) => void
}

export type Props = ValueProps & EventProps

export type State = {
  envId?: string,
  resourceId?: string,
  versionId?: string
}

class BuiltInDashboardLandingPage extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props)

    this.getMonitoringSelectorProps = this.getMonitoringSelectorProps.bind(this)
    this.environmentSelected = this.environmentSelected.bind(this)
    this.resourceSelected = this.resourceSelected.bind(this)
    this.versionSelected = this.versionSelected.bind(this)
    this.onViewClick = this.onViewClick.bind(this)

    this.state = {
      envId: props.environmentSelector.selected ? props.environmentSelector.selected.id : undefined,
      resourceId: props.resourceSelector.selected ? getResourceId(props.resourceSelector.selected) : undefined,
      versionId: props.versionSelector.selected ? props.versionSelector.selected.id.toString() : undefined
    }
  }

  environmentSelected(environmentId: string) {
    this.props.environmentSelected(environmentId, this.props.environmentSelector.resources)
    this.setState({...this.state, envId: environmentId, resourceId: undefined})
  }

  resourceSelected(resourceId: string, resourceType: ResourceType) {
    this.props.resourceSelected(resourceId, resourceType, this.props.resourceSelector.resources)
    this.setState({...this.state, resourceId: resourceId})
  }

  versionSelected(versionId: string) {
    const {versionSelector} = this.props
    if (versionSelector.resources) {
      this.props.versionSelected(versionId, versionSelector.resources)
      this.setState({...this.state, versionId: versionId})
    }
  }

  onViewClick() {
    const {envId, resourceId, versionId} = this.state
    if (envId && resourceId) this.props.onView(this.props.organizationId, envId, resourceId, versionId)
  }

  getViewButtonProps(environmentSelector: EnvironmentSelectorProps, resourceSelector: ResourceSelectorProps, versionSelector: VersionSelectorProps) {
    const environmentSelected = environmentSelector.selected !== undefined
    const resourceSelected = resourceSelector.selected !== undefined && this.state.resourceId !== undefined
    const isResourceAPI = resourceSelector.selected && resourceSelector.selected.resourceType === ResourceType.API
    const versionSelected = isResourceAPI ? versionSelector.selected !== undefined : true
    const isEnabled = environmentSelected && resourceSelected && versionSelected

    return {
      className: 'built-in-landing-page-view-button',
      kind: 'primary' as 'primary',
      testId: 'view-resource-btn',
      disabled: !isEnabled,
      onClick: this.onViewClick
    }
  }

  getMonitoringSelectorProps(envSelector: EnvironmentSelectorProps, resourceSelector: ResourceSelectorProps, versionSelector: VersionSelectorProps) {
    return {
      environmentSelector: envSelector,
      resourceSelector: resourceSelector,
      versionSelector: versionSelector,
      environmentSelected: this.environmentSelected,
      resourceSelected: this.resourceSelected,
      versionSelected: this.versionSelected
    }
  }

  componentWillMount(): void {
    this.props.checkBuiltInDashboardsState()
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, prevContext: any): void {
    const {environmentSelector, resourceSelector, versionSelector} = this.props
    if (prevProps.environmentSelector.selected !== environmentSelector.selected && environmentSelector.selected) {
      this.setState({...this.state, envId: environmentSelector.selected.id})
    }
    if (prevProps.resourceSelector.selected !== resourceSelector.selected && resourceSelector.selected) {
      this.setState({...this.state, resourceId: getResourceId(resourceSelector.selected)})
    }
    if (prevProps.versionSelector.selected !== versionSelector.selected && versionSelector.selected) {
      this.setState({...this.state, versionId: versionSelector.selected.id.toString()})
    }
  }

  render() {
    const {environmentSelector, resourceSelector, versionSelector} = this.props
    const envSelectorProps = transformToEnvironmentSelectorProps(environmentSelector, 25)
    const resourceSelectorProps = transformToResourceSelectorProps(resourceSelector, 40)
    const versionSelectorProps = transformToVersionSelectorProps(versionSelector, 35)
    const monitoringSelectorProps = this.getMonitoringSelectorProps(envSelectorProps, resourceSelectorProps, versionSelectorProps)
    const viewButtonProps = this.getViewButtonProps(envSelectorProps, resourceSelectorProps, versionSelectorProps)
    return (
      <div className={'built-in-landing-page-container'}>
        <AnnouncementBanner icon={{name: 'warning-filled', fill: '#856404', size: 22}} type="warning"
          basicMessage="Built-in dashboards reach End of Life (EOL) on October 15, 2026, and Anypoint Insights replaces them. The Classic view in Anypoint Monitoring is also scheduled for retirement on the same day." />
        <CertificatesExpiryBanner />
        <div className={'built-in-landing-page-header'}>
          <span className={'built-in-landing-page-title'}>Using built-in dashboards</span>
          <span className={'built-in-landing-page-subtitle'}>
              Dashboards are full of useful metrics, and there’s one for each of your applications and APIs.
          </span>
          <span className={'built-in-landing-page-subtitle'}>
              <a href={'https://docs.mulesoft.com/monitoring'} target={'_blank'}>Visit the documentation</a> to learn more.
          </span>
        </div>
        <div className={'built-in-landing-page-selector-container'}>
          <div className="built-in-landing-page-selector">
            <MonitoringSelector {...monitoringSelectorProps} />
            <div className={'built-in-landing-page-view-button'}>
              <Button {...viewButtonProps}>View</Button>
            </div>
          </div>
          <div className="built-in-landing-page-footer-text supported-runtimes">
            <SupportedRuntimesTooltipResolver/>
          </div>
        </div>
        <div className={'built-in-landing-page-image'}>
          <BuiltinSvg/>
        </div>
      </div>
    )
  }
}

export default BuiltInDashboardLandingPage
